import type {
  NoeVaultSnapInstance,
  SnapApplicationContext,
  SnapBrowserBootstrap,
  SnapCreateOptions,
  SnapError,
  SnapClaimInviteRequest,
  SnapClaimInviteResult,
  SnapClaimOffer,
  SnapEventHandler,
  SnapEventName,
  SnapSubmitInput,
  SnapSubmitResult
} from './types';
import { SnapDenyCodes, SNAP_PACKAGE_ID, SNAP_PACKAGE_VERSION } from './types';
import { SnapEventBus } from './events/eventBus';
import {
  assertAuthorizationNotPersisted,
  defaultPolicies,
  validateBootstrap,
  type BootstrapValidationResult
} from './bootstrap/validate';
import { AuthorizationMemory } from './auth/memory';
import { sanitizeApplicationContext, collectBrowserContext, buildSubmissionContext } from './context/contextModel';
import { AnnotationSession } from './annotation/annotationSession';
import { captureViewport, canvasToPngBlob } from './capture/screenshot';
import { preflightAttachments } from './attachments/preflight';
import { authorizeCreate, submitSupportRequest } from './submit/pipeline';
import { resolveTheme } from './theme/theme';
import { SnapUiHost } from './ui/host';
import { createSnapError, DefaultDeny } from './security/sanitize';
import { CustomerMessages } from './security/customerErrors';
import { REDACTION_LIMITATIONS } from './redaction/neverCapture';
import {
  addRequestComment,
  createConversationPoller,
  getRequestDetail,
  listMyRequests
} from './requests/myRequests';
import { validateRequestComposer } from './composer/requestComposer';
import { requestAccountClaim } from './claim/accountClaim';

const GLOBAL_INSTANCE_KEY = '__noevaultSnapActiveRoot';

interface ActiveMarker {
  rootId: string;
}

function getMount(mount?: HTMLElement | string): HTMLElement {
  if (!mount) return document.body;
  if (typeof mount === 'string') {
    const el = document.querySelector(mount);
    if (!(el instanceof HTMLElement)) {
      throw createSnapError(
        'configuration_invalid',
        SnapDenyCodes.ConfigurationInvalid,
        `Mount selector '${mount}' did not match an element.`
      );
    }
    return el;
  }
  return mount;
}

class NoeVaultSnap implements NoeVaultSnapInstance {
  private readonly options: SnapCreateOptions;
  private readonly events = new SnapEventBus();
  private readonly auth = new AuthorizationMemory();
  private readonly annotation = new AnnotationSession();
  private readonly now: () => Date;
  private readonly fetchImpl: typeof fetch;

  private destroyed = false;
  private initialized = false;
  private openState = false;
  private submitting = false;
  private validated: BootstrapValidationResult | null = null;
  private appContext: Record<string, unknown> = {};
  private pendingFiles: File[] = [];
  private ui: SnapUiHost | null = null;
  private unbindAnnotation: (() => void) | null = null;
  private captureBlob: Blob | null = null;
  private userInitiatedCapture = false;
  private conversationPoller: ReturnType<typeof createConversationPoller> | null = null;

  constructor(options: SnapCreateOptions = {}) {
    this.options = options;
    this.now = options.now ?? (() => new Date());
    this.fetchImpl = options.fetch ?? fetch.bind(globalThis);

    if (options.context) {
      const sanitized = sanitizeApplicationContext(options.context);
      if (!sanitized.ok) throw sanitized.error;
      this.appContext = sanitized.context;
    }
  }

  async initialize(bootstrap?: SnapBrowserBootstrap): Promise<void> {
    this.ensureAlive();
    const raw = bootstrap ?? this.options.bootstrap;
    if (!raw) {
      throw createSnapError(
        'bootstrap_invalid',
        SnapDenyCodes.BootstrapInvalid,
        'Bootstrap projection is required to initialize.'
      );
    }

    const validation = validateBootstrap(raw, this.now);
    if (!validation.ok) {
      if (validation.error.kind === 'bootstrap_expired') {
        this.events.emit('bootstrapExpired', { error: validation.error });
      }
      throw validation.error;
    }

    // Soft-disable after init is allowed to initialize UI but create will deny.
    this.validated = validation;
    this.auth.set(validation.bootstrap.authorization);
    assertAuthorizationNotPersisted(this.auth.get());

    if (!this.options.apiBaseUrl) {
      throw createSnapError(
        'configuration_invalid',
        SnapDenyCodes.ConfigurationInvalid,
        'apiBaseUrl is required.'
      );
    }

    this.mountUi(validation);
    this.initialized = true;
    this.events.emit('initialized', {
      correlationId: validation.bootstrap.correlationId
    });
    this.events.emit('ready', {
      correlationId: validation.bootstrap.correlationId
    });
  }

  async init(bootstrap?: SnapBrowserBootstrap): Promise<void> {
    return this.initialize(bootstrap);
  }

  open(): void {
    this.ensureReady();
    if (this.openState) {
      this.ui?.showEntry();
      return;
    }
    this.ui?.open();
    this.openState = true;
    this.events.emit('opened', {});
  }

  openReport(): void {
    this.open();
    this.ui?.showReport();
  }

  openMyRequests(): Promise<void> {
    this.open();
    return this.loadMyRequests();
  }

  close(reason?: string): void {
    this.ensureAlive();
    if (!this.openState) return;
    this.stopConversationPolling();
    this.ui?.close();
    this.openState = false;
    this.events.emit('closed', { reason });
  }

  async refreshBootstrap(bootstrap?: SnapBrowserBootstrap): Promise<void> {
    this.ensureAlive();
    let next = bootstrap;
    if (!next && this.options.refreshBootstrap) {
      next = await this.options.refreshBootstrap();
    }
    if (!next) {
      throw createSnapError(
        'bootstrap_invalid',
        SnapDenyCodes.BootstrapInvalid,
        'No bootstrap provided for refresh.'
      );
    }
    const validation = validateBootstrap(next, this.now);
    if (!validation.ok) {
      if (validation.error.kind === 'bootstrap_expired') {
        this.events.emit('bootstrapExpired', { error: validation.error });
        this.events.emit('sessionExpired', { error: validation.error });
      }
      throw validation.error;
    }
    this.validated = validation;
    this.auth.set(validation.bootstrap.authorization);
    assertAuthorizationNotPersisted(this.auth.get());
    this.applyExperience(validation);
  }

  async refreshContext(bootstrap?: SnapBrowserBootstrap): Promise<void> {
    return this.refreshBootstrap(bootstrap);
  }

  getClaimOffer(): SnapClaimOffer | null {
    return this.validated?.bootstrap.claim ?? null;
  }

  async requestAccountClaim(request: SnapClaimInviteRequest = {}): Promise<SnapClaimInviteResult> {
    this.ensureReady();
    const validation = this.validated!;
    const auth = this.auth.get();
    if (!auth) {
      const error = DefaultDeny.missingAuth(validation.bootstrap.correlationId);
      this.events.emit('unauthorized', { error });
      throw error;
    }

    const result = await requestAccountClaim({
      apiBaseUrl: this.options.apiBaseUrl!,
      endpoints: validation.defaults.endpoints,
      claim: validation.bootstrap.claim,
      authorization: auth,
      correlationId: request.correlationId || validation.bootstrap.correlationId,
      email: request.email,
      fetchImpl: this.fetchImpl,
      signal: this.options.signal
    });

    if (!result.ok) {
      this.events.emit('error', { error: result.error });
      throw result.error;
    }

    return result.result;
  }

  setContext(context: SnapApplicationContext): void {
    this.ensureAlive();
    const sanitized = sanitizeApplicationContext(context);
    if (!sanitized.ok) throw sanitized.error;
    this.appContext = sanitized.context;
  }

  async submit(input: SnapSubmitInput): Promise<SnapSubmitResult> {
    this.ensureReady();
    if (this.submitting) {
      return {
        success: false,
        correlationId: this.validated!.bootstrap.correlationId,
        error: createSnapError(
          'submission_denied',
          SnapDenyCodes.SubmissionInProgress,
          'A submission is already in progress.'
        )
      };
    }

    const validation = this.validated!;
    const correlationId = validation.bootstrap.correlationId;
    const auth = this.auth.get();
    if (!auth) {
      const error = DefaultDeny.missingAuth(correlationId);
      this.events.emit('submissionDenied', { error });
      return { success: false, correlationId, error };
    }

    if (this.now().getTime() >= Date.parse(validation.bootstrap.expiresAtUtc)) {
      const error = DefaultDeny.expired(correlationId);
      this.events.emit('bootstrapExpired', { error });
      this.events.emit('submissionDenied', { error });
      return { success: false, correlationId, error };
    }

    if (
      validation.bootstrap.capability.softDisabled ||
      !validation.bootstrap.capability.createAllowed ||
      !validation.bootstrap.capability.available
    ) {
      const error = createSnapError(
        'soft_disabled',
        validation.bootstrap.capability.softDisabled
          ? SnapDenyCodes.SoftDisabled
          : SnapDenyCodes.EntitlementUnavailable,
        'Snap create is not currently allowed for this capability state.',
        { correlationId, userSafe: true }
      );
      this.events.emit('submissionDenied', { error });
      return { success: false, correlationId, error };
    }

    const title = (input.title || '').trim();
    const description = (input.description || '').trim();
    const composerCheck = validateRequestComposer({
      title,
      description,
      priority: Number(input.priority ?? 3),
      categoryId: input.categoryId ?? null,
      screenshotDescription: (input.screenshotDescription || '').trim(),
      includeScreenshot: !!(this.captureBlob && input.includeScreenshot !== false)
    });
    if (!composerCheck.ok) {
      const error = createSnapError(
        'configuration_invalid',
        SnapDenyCodes.ConfigurationInvalid,
        composerCheck.errors[0] || 'Request is incomplete.',
        { correlationId }
      );
      this.events.emit('submissionDenied', { error });
      this.ui?.setStatus(error.message, 'error');
      return { success: false, correlationId, error };
    }

    const files = input.attachments ?? this.pendingFiles;
    const preflight = preflightAttachments(files, validation.defaults.evidenceConstraints);
    if (!preflight.ok) {
      this.events.emit('submissionDenied', { error: preflight.error });
      return { success: false, correlationId, error: preflight.error };
    }

    this.submitting = true;
    this.ui?.setSubmitting(true);
    this.events.emit('submissionStarted', { correlationId });

    try {
      const authz = await authorizeCreate({
        apiBaseUrl: this.options.apiBaseUrl!,
        endpoints: validation.defaults.endpoints,
        authorization: auth,
        correlationId,
        fetchImpl: this.fetchImpl,
        signal: input.signal ?? this.options.signal
      });
      if (!authz.ok) {
        if (authz.error.kind === 'bootstrap_expired') {
          this.events.emit('bootstrapExpired', { error: authz.error });
        }
        this.events.emit('submissionDenied', { error: authz.error });
        return { success: false, correlationId, error: authz.error };
      }

      const browser = collectBrowserContext({
        includePageUrl: validation.defaults.capturePolicy.includePageUrl,
        includeQueryString: validation.defaults.capturePolicy.includeQueryString,
        includePageTitle: validation.defaults.capturePolicy.includePageTitle,
        correlationId,
        now: this.now
      });

      const layered = buildSubmissionContext({
        server: validation.bootstrap.context,
        application: this.appContext,
        browser
      });

      let screenshot: Blob | null = null;
      if (input.includeScreenshot !== false) {
        // Prefer the annotated surface so crop/redact/drawing reach the evidence pipeline.
        const flattened = this.annotation.flatten();
        if (flattened) {
          screenshot = await canvasToPngBlob(flattened);
          this.captureBlob = screenshot;
        } else if (this.captureBlob) {
          screenshot = this.captureBlob;
        }
      }

      const metadata = {
        captureVersion: '1',
        captureScope: 'Viewport',
        packageId: SNAP_PACKAGE_ID,
        packageVersion: SNAP_PACKAGE_VERSION,
        redactionApplied: true,
        annotationApplied: this.annotation.hasAnnotation,
        redactionLimitations: REDACTION_LIMITATIONS,
        description: (input.screenshotDescription || '').trim() || undefined,
        altText: (input.screenshotDescription || '').trim() || undefined,
        context: layered
      };

      const result = await submitSupportRequest({
        apiBaseUrl: this.options.apiBaseUrl!,
        endpoints: validation.defaults.endpoints,
        authorization: auth,
        correlationId,
        title,
        description,
        priority: input.priority ?? validation.defaults.experience.defaultPriority ?? 3,
        categoryId: input.categoryId,
        contextJson: JSON.stringify(layered),
        metadataJson: JSON.stringify(metadata),
        screenshot,
        attachments: preflight.files,
        fetchImpl: this.fetchImpl,
        signal: input.signal ?? this.options.signal,
        serverContext: validation.bootstrap.context
      });

      if (!result.success) {
        if (result.error?.kind === 'transport_failed') {
          this.events.emit('submissionFailed', { error: result.error });
          this.events.emit('error', { error: result.error });
        } else if (result.error) {
          this.events.emit('submissionDenied', { error: result.error });
          this.events.emit('error', { error: result.error });
          if (result.error.kind === 'bootstrap_expired') {
            this.events.emit('bootstrapExpired', { error: result.error });
            this.events.emit('sessionExpired', { error: result.error });
          }
          if (result.error.kind === 'authorization_missing') {
            this.events.emit('unauthorized', { error: result.error });
          }
        }
        this.ui?.setStatus(result.error?.message || CustomerMessages.submissionFailed, 'error');
        return result;
      }

      this.events.emit('submissionSucceeded', {
        ticketId: result.ticketId,
        ticketNumber: result.ticketNumber,
        correlationId: result.correlationId
      });
      this.events.emit('requestCreated', {
        ticketId: result.ticketId,
        ticketNumber: result.ticketNumber,
        correlationId: result.correlationId
      });
      this.ui?.setStatus(
        result.ticketNumber
          ? `Request ${result.ticketNumber} submitted.`
          : CustomerMessages.requestSubmitted,
        'success'
      );
      // Preserve capture for retry only until success; then clear and open My Requests.
      this.captureBlob = null;
      this.annotation.clearMemory();
      this.pendingFiles = [];
      this.ui?.clearPreview();
      void this.loadMyRequests();
      return result;
    } finally {
      this.submitting = false;
      this.ui?.setSubmitting(false);
    }
  }

  destroy(): void {
    if (this.destroyed) return;
    this.stopConversationPolling();
    this.close('destroy');
    this.unbindAnnotation?.();
    this.unbindAnnotation = null;
    this.annotation.clearMemory();
    this.captureBlob = null;
    this.pendingFiles = [];
    this.auth.clear();
    this.ui?.destroy();
    this.ui = null;
    this.events.emit('destroyed', {});
    this.events.clear();
    this.validated = null;
    this.initialized = false;
    this.destroyed = true;
    clearActiveMarker();
  }

  on<K extends SnapEventName>(event: K, handler: SnapEventHandler<K>): void {
    this.events.on(event, handler);
  }

  off<K extends SnapEventName>(event: K, handler: SnapEventHandler<K>): void {
    this.events.off(event, handler);
  }

  getCorrelationId(): string | null {
    return this.validated?.bootstrap.correlationId ?? null;
  }

  isOpen(): boolean {
    return this.openState;
  }

  isDestroyed(): boolean {
    return this.destroyed;
  }

  /** Test/harness helper — never log raw authorization. */
  getAuthorizationLogSafe(): string {
    return this.auth.toLogSafe();
  }

  private mountUi(validation: BootstrapValidationResult): void {
    // Prevent duplicate launchers for the same document.
    const existing = document.getElementById('nv-snap-root');
    if (existing) {
      existing.remove();
    }
    clearActiveMarker();

    const theme = resolveTheme({
      ...this.options.theme,
      launcherPosition:
        this.options.theme?.launcherPosition ||
        (validation.defaults.ui.defaultPlacement as never)
    });

    const mount = getMount(this.options.mount);
    this.ui = new SnapUiHost(
      mount,
      theme,
      {
        onOpenRequest: () => this.open(),
        onCloseRequest: () => this.close('user'),
        onOpenReport: () => this.openReport(),
        onOpenMyRequests: () => this.openMyRequests(),
        onBackToEntry: () => this.ui?.showEntry(),
        onCaptureRequest: () => {
          void this.handleCapture();
        },
        onRetakeRequest: () => {
          this.captureBlob = null;
          this.annotation.clearMemory();
          this.ui?.clearPreview();
          void this.handleCapture();
        },
        onSubmitRequest: () => {
          const values = this.ui?.getFormValues();
          void this.submit({
            title: values?.title || '',
            description: values?.description || '',
            priority: values?.priority,
            categoryId: values?.categoryId,
            screenshotDescription: values?.screenshotDescription,
            attachments: this.pendingFiles,
            includeScreenshot: !!this.captureBlob
          });
        },
        onFilesChanged: (files) => {
          this.pendingFiles = files;
        },
        onSelectRequest: (ticketId) => {
          void this.loadRequestDetail(ticketId);
        },
        onAddComment: (ticketId, comment) => {
          void this.postComment(ticketId, comment);
        },
        onRefreshDetail: (ticketId) => {
          void this.loadRequestDetail(ticketId);
        },
        onClaimAccount: (email) => {
          void this.handleClaimAccount(email);
        }
      },
      {
        session: this.annotation,
        evidenceConstraints: validation.defaults.evidenceConstraints
      }
    );

    this.applyExperience(validation);
    const showLauncher =
      this.options.showLauncher !== false && validation.defaults.ui.launcherEnabled;
    this.ui.setLauncherVisible(showLauncher);

    // SnapScreenEditor owns annotation canvas binding via the shared session.
    this.unbindAnnotation = null;
    markActive(this.ui.root.id);
  }

  private applyExperience(validation: BootstrapValidationResult): void {
    const cap = validation.bootstrap.capability;
    this.ui?.setExperience(validation.defaults.experience);
    this.ui?.setCapabilities({
      createAllowed: cap.createAllowed,
      myRequestsAllowed: cap.myRequestsAllowed !== false,
      captureAllowed:
        cap.captureAllowed !== false && validation.defaults.capturePolicy.screenshotEnabled
    });
    this.ui?.setConsent(validation.defaults.ui.consentMessage || '');
    this.ui?.setProductLabels({
      productName: validation.defaults.ui.productName,
      reportLabel: validation.defaults.ui.reportLabel,
      myRequestsLabel: validation.defaults.ui.myRequestsLabel
    });
    this.ui?.setScreenshotEnabled(
      cap.captureAllowed !== false && validation.defaults.capturePolicy.screenshotEnabled
    );
    this.ui?.setClaimOffer(validation.bootstrap.claim);
  }

  /**
   * PR-8.7D.5 — ask the server to email a claim link and one-time code. The SDK never receives the
   * proof, so a compromised page cannot complete a claim; failures stay non-fatal because claiming
   * is optional.
   */
  private async handleClaimAccount(email?: string): Promise<void> {
    const offer = this.getClaimOffer();
    if (offer?.requiresEmail && !email) {
      this.ui?.setClaimStatus('Enter your email address to continue.', 'error');
      return;
    }

    this.ui?.setClaiming(true);
    this.ui?.setClaimStatus('Sending your claim link…');
    try {
      const result = await this.requestAccountClaim({ email });
      this.ui?.setClaimStatus(
        result.maskedEmail
          ? `We sent a claim link and verification code to ${result.maskedEmail}.`
          : result.message || 'We sent a claim link and verification code to your email address.',
        'success'
      );
    } catch (error) {
      const snapError = error as SnapError;
      this.ui?.setClaimStatus(
        snapError?.userSafe && snapError.message
          ? snapError.message
          : CustomerMessages.temporarilyUnavailable,
        'error'
      );
    } finally {
      this.ui?.setClaiming(false);
    }
  }

  private async loadMyRequests(): Promise<void> {
    this.ensureReady();
    this.stopConversationPolling();
    const validation = this.validated!;
    const auth = this.auth.get();
    if (!auth) {
      const error = DefaultDeny.missingAuth(validation.bootstrap.correlationId);
      this.events.emit('unauthorized', { error });
      this.ui?.setStatus(CustomerMessages.applicationSignInRequired, 'error');
      return;
    }
    this.ui?.showMyRequestsLoading();
    this.ui?.setStatus('Loading your requests…');
    const result = await listMyRequests({
      apiBaseUrl: this.options.apiBaseUrl!,
      endpoints: validation.defaults.endpoints,
      authorization: auth,
      correlationId: validation.bootstrap.correlationId,
      fetchImpl: this.fetchImpl,
      signal: this.options.signal
    });
    if (!result.ok) {
      this.events.emit('error', { error: result.error });
      this.ui?.setStatus(result.error.message || CustomerMessages.myRequestsUnavailable, 'error');
      this.ui?.showMyRequestsError(result.error.message || CustomerMessages.myRequestsUnavailable);
      return;
    }
    this.ui?.showMyRequests(result.items);
    this.ui?.setStatus('', 'info');
  }

  private async loadRequestDetail(ticketId: number | string, opts?: { quiet?: boolean }): Promise<void> {
    this.ensureReady();
    const validation = this.validated!;
    const auth = this.auth.get();
    if (!auth) return;
    if (!opts?.quiet) this.ui?.setStatus('Loading request…');
    const result = await getRequestDetail({
      apiBaseUrl: this.options.apiBaseUrl!,
      endpoints: validation.defaults.endpoints,
      authorization: auth,
      correlationId: validation.bootstrap.correlationId,
      ticketId,
      fetchImpl: this.fetchImpl,
      signal: this.options.signal
    });
    if (!result.ok) {
      this.events.emit('error', { error: result.error });
      if (!opts?.quiet) {
        this.ui?.setStatus(result.error.message || CustomerMessages.myRequestsUnavailable, 'error');
      }
      return;
    }
    this.ui?.showRequestDetail(result.detail);
    if (!opts?.quiet) {
      this.ui?.setStatus('', 'info');
      this.startConversationPolling(ticketId);
    }
  }

  private startConversationPolling(ticketId: number | string): void {
    this.stopConversationPolling();
    this.conversationPoller = createConversationPoller({
      intervalMs: 8000,
      refresh: async () => {
        if (this.ui?.getActiveTicketId() !== ticketId) {
          this.stopConversationPolling();
          return;
        }
        await this.loadRequestDetail(ticketId, { quiet: true });
      }
    });
    this.conversationPoller.start();
    this.ui?.setConversationLive(true);
  }

  private stopConversationPolling(): void {
    this.conversationPoller?.stop();
    this.conversationPoller = null;
    this.ui?.setConversationLive(false);
  }

  private async postComment(ticketId: number | string, comment: string): Promise<void> {
    this.ensureReady();
    if (!comment.trim()) {
      this.ui?.setStatus('Enter a message before sending.', 'error');
      return;
    }
    const validation = this.validated!;
    const auth = this.auth.get();
    if (!auth) return;
    const result = await addRequestComment({
      apiBaseUrl: this.options.apiBaseUrl!,
      endpoints: validation.defaults.endpoints,
      authorization: auth,
      correlationId: validation.bootstrap.correlationId,
      ticketId,
      comment: comment.trim(),
      fetchImpl: this.fetchImpl,
      signal: this.options.signal
    });
    if (!result.ok) {
      this.events.emit('error', { error: result.error });
      this.ui?.setStatus(result.error.message || CustomerMessages.submissionFailed, 'error');
      return;
    }
    this.ui?.setStatus('Message sent.', 'success');
    await this.loadRequestDetail(ticketId);
  }

  private async handleCapture(): Promise<void> {
    this.ensureReady();
    const validation = this.validated!;
    if (!validation.defaults.capturePolicy.screenshotEnabled) {
      const error = createSnapError(
        'capture_disabled',
        SnapDenyCodes.CaptureDisabled,
        'Screenshot capture is disabled by server policy.'
      );
      this.events.emit('captureFailed', { error });
      this.ui?.setStatus(error.message, 'error');
      return;
    }

    this.userInitiatedCapture = true;
    this.events.emit('captureStarted', {});
    this.ui?.setStatus('Capturing current viewport…');
    try {
      const result = await captureViewport({
        capturePolicy: validation.defaults.capturePolicy,
        privacyPolicy: validation.defaults.privacyPolicy,
        userInitiated: this.userInitiatedCapture,
        signal: this.options.signal
      });
      this.annotation.setSource(result.canvas);
      this.ui?.showPreview(result.canvas);
      this.captureBlob = await canvasToPngBlob(result.canvas);
      this.events.emit('captureCompleted', {
        width: result.width,
        height: result.height,
        redacted: result.redacted
      });
      this.ui?.setStatus('Preview ready. Cover sensitive areas if needed, then submit.', 'info');
    } catch (err) {
      const error =
        err && typeof err === 'object' && 'kind' in err
          ? (err as ReturnType<typeof createSnapError>)
          : createSnapError(
              'capture_failed',
              SnapDenyCodes.CaptureFailed,
              'Screenshot capture failed.'
            );
      this.events.emit('captureFailed', { error });
      this.ui?.setStatus(error.message, 'error');
    } finally {
      this.userInitiatedCapture = false;
    }
  }

  private ensureAlive(): void {
    if (this.destroyed) {
      throw createSnapError(
        'destroyed',
        SnapDenyCodes.ConfigurationInvalid,
        'Snap instance has been destroyed.'
      );
    }
  }

  private ensureReady(): void {
    this.ensureAlive();
    if (!this.initialized || !this.validated) {
      throw createSnapError(
        'configuration_invalid',
        SnapDenyCodes.ConfigurationInvalid,
        'Snap has not been initialized.'
      );
    }
  }
}

function markActive(rootId: string): void {
  (globalThis as unknown as Record<string, ActiveMarker>)[GLOBAL_INSTANCE_KEY] = {
    rootId
  };
}

function clearActiveMarker(): void {
  delete (globalThis as unknown as Record<string, unknown>)[GLOBAL_INSTANCE_KEY];
}

/**
 * Create a framework-independent NoeVault Snap browser client.
 * Accepts only browser-safe bootstrap data — never AppKey or persistent secrets.
 */
export function createNoeVaultSnap(options: SnapCreateOptions = {}): NoeVaultSnapInstance {
  // Reject AppKey at construction boundary even if nested oddly.
  const anyOpts = options as Record<string, unknown>;
  if (anyOpts.appKey || anyOpts.AppKey || anyOpts.appSecret || anyOpts.AppSecret) {
    throw DefaultDeny.appKey();
  }
  return new NoeVaultSnap(options);
}

export { defaultPolicies, REDACTION_LIMITATIONS, SNAP_PACKAGE_ID, SNAP_PACKAGE_VERSION };
